import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { 
  LayoutDashboard, 
  Store, 
  Users, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  ChefHat,
  CreditCard,
  Utensils,
  FileText,
  BarChart3
} from "lucide-react";
import { cn } from "../../lib/utils";

const AdminLayout = ({ children }) => {
  const { user, logout, isSuperAdmin, isOwner, isManager, isKitchen, isCashier } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Define navigation items based on role
  const getNavItems = () => {
    if (isSuperAdmin) {
      return [
        { href: "/admin", icon: LayoutDashboard, label: "Dashboard" },
        { href: "/admin/restaurants", icon: Store, label: "Restaurants" },
        { href: "/admin/users", icon: Users, label: "Users" },
      ];
    }
    
    if (isOwner || isManager) {
      return [
        { href: "/manager", icon: LayoutDashboard, label: "Dashboard" },
        { href: "/manager/analytics", icon: BarChart3, label: "Analytics" },
        { href: "/manager/menu", icon: Utensils, label: "Menu" },
        { href: "/manager/tables", icon: ChefHat, label: "QR & Tables" },
        { href: "/manager/orders", icon: FileText, label: "Orders" },
        { href: "/manager/staff", icon: Users, label: "Staff" },
        { href: "/manager/reports", icon: FileText, label: "Reports" },
        { href: "/manager/settings", icon: Settings, label: "Settings" },
      ];
    }
    
    if (isKitchen) {
      return [
        { href: "/kitchen", icon: ChefHat, label: "All Orders" },
        { href: "/kitchen/food", icon: Utensils, label: "Food Kitchen" },
        { href: "/kitchen/drinks", icon: ChefHat, label: "Drinks Kitchen" },
      ];
    }
    
    if (isCashier) {
      return [
        { href: "/cashier", icon: CreditCard, label: "Cashier" },
        { href: "/cashier/reports", icon: BarChart3, label: "Reports" },
      ];
    }
    
    return [];
  };

  const navItems = getNavItems();

  const NavLink = ({ item }) => {
    const isActive = location.pathname === item.href;
    return (
      <Link
        to={item.href}
        className={cn(
          "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors",
          isActive
            ? "bg-primary text-white"
            : "text-muted-foreground hover:bg-muted hover:text-foreground"
        )}
        onClick={() => setSidebarOpen(false)}
        data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
      >
        <item.icon className="w-5 h-5" />
        <span className="font-medium">{item.label}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-border z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <Utensils className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold">Dalabsom</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 h-full w-64 bg-white border-r border-border z-40 transition-transform duration-300",
          "lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Logo */}
        <div className="h-16 flex items-center gap-3 px-4 border-b border-border">
          <div className="w-10 h-10 bg-sky-500 rounded-xl flex items-center justify-center">
            <Utensils className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-foreground">Dalabsom</h1>
            <p className="text-xs text-muted-foreground capitalize">{user?.role?.replace("_", " ")}</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink key={item.href} item={item} />
          ))}
        </nav>

        {/* User Section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border bg-white">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
              <span className="text-sm font-semibold text-muted-foreground">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="outline"
            className="w-full justify-start gap-2"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-4 lg:p-6">{children}</div>
      </main>
    </div>
  );
};

export default AdminLayout;
